/**
 * Однократная загрузка внешнего скрипта по требованию.
 *
 * Сторонние SDK (карты, VK ID) раньше висели тегами в <head> обоих лейаутов и
 * грузились на КАЖДОЙ странице — включая главную, где ни карта, ни вход через VK
 * не нужны. Это сотни килобайт чужого JS в критическом пути: прямой удар по
 * Core Web Vitals, а значит и по позициям в выдаче.
 *
 * Промис кэшируется по src: параллельные вызовы получают один и тот же тег.
 * Ошибка кэш сбрасывает — следующая попытка (пользователь открыл модалку заново)
 * должна снова попробовать загрузить скрипт, а не падать мгновенно.
 */
import { consola } from 'consola'

const logger = consola.withTag('external-script')

const pending = new Map<string, Promise<void>>()

export interface LoadScriptOptions {
  /** id тега — чтобы найти уже вставленный скрипт и не продублировать его. */
  id: string
  /** Сколько ждём загрузки, прежде чем считать её проваленной. */
  timeoutMs?: number
}

export function loadScriptOnce(src: string, options: LoadScriptOptions): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error(`Скрипт ${options.id} недоступен на сервере`))
  }

  const cached = pending.get(src)
  if (cached) return cached

  const timeoutMs = options.timeoutMs ?? 15_000

  const promise = new Promise<void>((resolve, reject) => {
    const timeout = setTimeout(() => {
      pending.delete(src)
      const error = new Error(`${options.id}: таймаут загрузки`)
      logger.error(error)
      reject(error)
    }, timeoutMs)

    const onLoad = () => {
      clearTimeout(timeout)
      resolve()
    }

    const onError = () => {
      clearTimeout(timeout)
      pending.delete(src)
      const error = new Error(`${options.id}: ошибка загрузки скрипта`)
      logger.error(error)
      reject(error)
    }

    // Тег мог поставить другой вызов, пока промис ещё не попал в кэш.
    const existing = document.querySelector<HTMLScriptElement>(`#${options.id}`)
    if (existing) {
      existing.addEventListener('load', onLoad, { once: true })
      existing.addEventListener('error', onError, { once: true })
      return
    }

    const script = document.createElement('script')
    script.id = options.id
    script.src = src
    script.async = true
    script.addEventListener('load', onLoad, { once: true })
    script.addEventListener('error', onError, { once: true })
    document.head.append(script)
  })

  pending.set(src, promise)
  return promise
}
